import { createReducer, on } from '@ngrx/store';
import * as NotificationActions from './notification.actions';
import { NotificationEntity } from './notification.models';

// ----------------------------------------------------------------------
// @ State
// ----------------------------------------------------------------------

export interface NotificationsState {
    notifications: NotificationEntity[];
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial State
// ----------------------------------------------------------------------

export const initialNotificationsState: NotificationsState = {
    notifications: [],
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducer
// ----------------------------------------------------------------------

export const notificationsReducer = createReducer(
    initialNotificationsState,

    // -----------------------------------------------------------
    // @ Load from API
    // -----------------------------------------------------------

    on(NotificationActions.loadNotifications, (state) => ({
        ...state,
        isLoading: true,
        error: null,
    })),

    on(
        NotificationActions.loadNotificationsSuccess,
        (state, { notifications }) => ({
            ...state,
            isLoading: false,
            notifications,
        })
    ),

    on(NotificationActions.loadNotificationsFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // -----------------------------------------------------------
    // @ Add (after duplicate check in effects)
    // -----------------------------------------------------------

    on(NotificationActions.createNotificationSuccess, (state, { data }) => ({
        ...state,
        notifications: [...state.notifications, data],
    })),

    // -----------------------------------------------------------
    // @ Update
    // -----------------------------------------------------------

    on(NotificationActions.updateNotificationSuccess, (state, { data }) => ({
        ...state,
        notifications: state.notifications.map((n) =>
            n.id === data.id ? data : n
        ),
    })),

    // -----------------------------------------------------------
    // @ Delete (via API)
    // -----------------------------------------------------------

    on(NotificationActions.deleteNotificationSuccess, (state, { id }) => ({
        ...state,
        notifications: state.notifications.filter((n) => n.id !== id),
    })),

    // -----------------------------------------------------------
    // @ Remove
    // -----------------------------------------------------------

    on(NotificationActions.removeNotification, (state, { id }) => ({
        ...state,
        notifications: state.notifications.filter((n) => n.id !== id),
    })),

    // -----------------------------------------------------------
    // @ Read state
    // -----------------------------------------------------------

    on(NotificationActions.markAsRead, (state, { id }) => ({
        ...state,
        notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
        ),
    })),

    on(NotificationActions.markAllAsRead, (state) => ({
        ...state,
        notifications: state.notifications.map((n) => ({ ...n, read: true })),
    })),

    // -----------------------------------------------------------
    // @ Clear
    // -----------------------------------------------------------

    on(NotificationActions.clearAll, (state) => ({
        ...state,
        notifications: [],
    })),

    on(NotificationActions.clearByCategory, (state, { category }) => ({
        ...state,
        notifications: state.notifications.filter(
            (n) => n.category !== category
        ),
    })),

    on(NotificationActions.clearByModule, (state, { module }) => ({
        ...state,
        notifications: state.notifications.filter((n) => n.module !== module),
    }))
);
